import { get as _get } from 'lodash';
import moment from 'moment';

import { RequestPropertyIds } from '../../../../data/BackendEntityConstants';
import { DCSP } from '../../../../data/BusinessConstants';
import { getQuoteTemplateType } from '../../../../data/RFXLineItemTemplate/RFXLineItemUtils';
import { Property, RFXRequest } from '../../../../interfaces/RFXBackendModel';
import {
  convertStringToRFXRequestState,
  DCSPEventSubTypes,
  DCSPRFXRequestInformation,
  EventSubTypeProps,
  ReworkProps,
  RFXRequestForm,
  RFXRequestGeneralInformation
} from '../../../../interfaces/RFXRequestForm';
import { getBusinessIdFromBackendModel, shouldEnableAutoNameGeneration } from '../../../BusinessUtils';
import {
  extractCategory,
  extractDefaultQuoteValidity,
  extractListPropertyOrElseDefault,
  extractNumericalPropertyOrElseDefault,
  extractPropertyOrElseDefault,
  extractRegion,
  extractSiteNameFBN,
  isCloneRequest
} from '../../../RFXUtils';
import { getWorkflowFromBackendModel } from '../../../WorkflowUtils';
import { convertDocuments, convertDocumentsToQuoteEntries, convertSuppliers } from '../../common/frontendConverter';
import { isValidEventSubtype } from './commonHelper';
import { convertCommodityInformationToFrontendModel } from './frontendHelper';

/**
 * Converts backend model RFXRequest to frontend model RFXRequestForm
 * @param rfxRequest RFXRequest
 */
export function convertRFXReqBackendModelToRFXRequestForm(rfxRequest: RFXRequest): RFXRequestForm {
  const needByDateStr = extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.EvaluationDate, '');
  const businessId = getBusinessIdFromBackendModel(rfxRequest);
  const workflow = getWorkflowFromBackendModel(rfxRequest);
  const siteNameFBNFieldExists = shouldEnableAutoNameGeneration(businessId, workflow);
  const quoteTemplaType = getQuoteTemplateType(businessId, workflow, rfxRequest.rfxLineItemClassId.value);

  const generalInformation: RFXRequestGeneralInformation = {
    userRfxId: '',
    fullRfxId: rfxRequest.rfxRequestRevisionedId.id.value,
    rfxType: rfxRequest.type,
    rfxName: rfxRequest.name,
    siteNameFBN: siteNameFBNFieldExists ? extractSiteNameFBN(rfxRequest) : null,
    businessId,
    workflow,
    beginDate: moment(Number(rfxRequest.beginAt)).format('YYYY-MM-DD'),
    beginTime: moment(Number(rfxRequest.beginAt)).format('HH:mm'),
    dueDate: moment(Number(rfxRequest.dueAt)).format('YYYY-MM-DD'),
    dueTime: moment(Number(rfxRequest.dueAt)).format('HH:mm'),
    needByDate: needByDateStr ? moment(Number(needByDateStr)).format('YYYY-MM-DD') : '',
    needByTime: needByDateStr ? moment(Number(needByDateStr)).format('HH:mm') : '',
    ...extractRegion(rfxRequest),
    organization: {
      label: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.Organization, ''),
      value: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.Organization, '')
    },
    businessUnit: {
      label: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.BusinessUnit, ''),
      value: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.BusinessUnit, '')
    },
    department: {
      label: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.Department, ''),
      value: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.Department, '')
    },
    ...extractCategory(rfxRequest.properties),
    // Adding subcategory extraction for DCSP
    ...(businessId === DCSP ? {
      subCategory: {
        label: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.SubCategory, ''),
        value: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.SubCategory, '')
      }
    } : {}),
    defaultQuoteValidity: extractDefaultQuoteValidity(rfxRequest.properties),
    project: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.Project, ''),
    simUrl: extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.SimUrl, ''),
    currency: { label: rfxRequest.currency, value: rfxRequest.currency },
    quoteTemplateType: quoteTemplaType,
    rfxLineItemClassId: rfxRequest.rfxLineItemClassId.value,
    overview: _get(rfxRequest, 'generalInformation.overview', ''),
    processDetails: _get(rfxRequest, 'generalInformation.processDetail', '')
  };

  if (businessId === DCSP && workflow === DCSP) {
    convertDCSPBackendPropertiesToGeneralInfomation(rfxRequest.properties, generalInformation);
  }

  return {
    state: convertStringToRFXRequestState(rfxRequest.lifeCycle.lifeCycleState.name),
    round: rfxRequest.rfxRequestRevisionedId.revision,
    rfxRequestVersion: rfxRequest.rfxRequestVersion,
    generalInformation,
    quotes: {
      quoteEntries: convertDocumentsToQuoteEntries(rfxRequest.documents),
      commodityInformation: convertCommodityInformationToFrontendModel(rfxRequest.commodityInformation),
      quoteTemplateGenerationStatus: undefined
    },
    suppliers: (isCloneRequest(window.location.pathname) && businessId === 'PAC')
      ? { supplierEntries: [], existingSupplierContactsMap: {} }
      : convertSuppliers(rfxRequest.suppliers, businessId, workflow),
    'co-managers': {
      coManagerEntries: extractListPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.CoManagers)
    },
    documents: convertDocuments(rfxRequest.documents),
    review: {},
    trackingInformation: {
      publishedAt: extractNumericalPropertyOrElseDefault(
        rfxRequest.properties,
        RequestPropertyIds.PublishedAt,
        undefined
      ),
      awardedAt: extractNumericalPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.AwardedAt, undefined)
    }
  };
}

function convertDCSPBackendPropertiesToGeneralInfomation(
  properties: Property[] | undefined,
  generalInformationInput: RFXRequestGeneralInformation
): void {
  if (properties === undefined) {
    return;
  }
  const generalInformation: DCSPRFXRequestInformation = generalInformationInput as DCSPRFXRequestInformation;

  // Sites
  const sites: string[] = extractListPropertyOrElseDefault<string>(properties, RequestPropertyIds.Sites);
  generalInformation.sites = sites.map((site) => ({ label: site, value: site }));

  // Build Id
  const buildId = extractPropertyOrElseDefault(properties, RequestPropertyIds.BuildId, '');
  if (buildId !== '') {
    generalInformation.buildId = buildId;
    generalInformation.isBuildIdAvailable = 'Yes';
  }
  const buildIdComments = extractPropertyOrElseDefault(properties, RequestPropertyIds.BuildIdComments, '');
  if (buildIdComments !== '') {
    generalInformation.buildIdComments = buildIdComments;
    generalInformation.isBuildIdAvailable = 'No';
  }

  // Estimated amount
  const estimatedAmount = extractPropertyOrElseDefault(properties, RequestPropertyIds.EstimatedAmount, '');
  if (estimatedAmount !== '') {
    generalInformation.estimatedAmount = estimatedAmount;
  }

  // Event sub type
  convertSubEventBackendModel(properties, generalInformation);
}

function convertSubEventBackendModel(properties: Property[], generalInformation: DCSPRFXRequestInformation): void {
  const option: string = extractPropertyOrElseDefault(properties, RequestPropertyIds.EventSubType, '');
  if (!isValidEventSubtype(option)) {
    throw Error('Enter a valid event sub type.');
  }

  const eventSubType: EventSubTypeProps = {
    option: { label: option, value: option }
  };

  generalInformation.eventSubType = eventSubType;

  switch (option) {
    case DCSPEventSubTypes.Competitive:
      const hasAlignmentDocument: string = extractPropertyOrElseDefault(
        properties,
        RequestPropertyIds.HasAlignmentDocument,
        ''
      );
      if (hasAlignmentDocument === 'Yes') {
        eventSubType.isAligned = 'Yes';
      } else {
        eventSubType.isAligned = 'No';
      }
      const nonAlignmentReason: string = extractPropertyOrElseDefault(
        properties,
        RequestPropertyIds.NonAlignmentReason,
        ''
      );
      eventSubType.nonAlignmentReason = nonAlignmentReason;
      break;
    case DCSPEventSubTypes.SSJ:
      const ssjLink: string = extractPropertyOrElseDefault(properties, RequestPropertyIds.SSJLink, '');
      if (ssjLink === '') {
        throw Error('SSJ link is required.');
      }
      eventSubType.ssjLink = ssjLink;
      break;
    case DCSPEventSubTypes.Repricing:
      eventSubType.repricing = extractReworkProperties(
        properties,
        RequestPropertyIds.RepricingSourceEventId,
        RequestPropertyIds.RepricingComments
      );
      break;
    case DCSPEventSubTypes.ContractRenewal:
      eventSubType.renewal = extractReworkProperties(
        properties,
        RequestPropertyIds.RenewalSourceEventId,
        RequestPropertyIds.RenewalComments
      );
      break;
    default:
      throw Error('Enter a valid event sub type.');
  }
}

function extractReworkProperties(
  properties: Property[],
  sourceEventIdPropertyId: string,
  commentsPropertyId: string
): ReworkProps {
  const sourceEventId: string = extractPropertyOrElseDefault(properties, sourceEventIdPropertyId, '');
  const comments: string = extractPropertyOrElseDefault(properties, commentsPropertyId, '');

  if (sourceEventId === '' && comments === '') {
    throw Error('Source event ID or comments are required for the event sub type.');
  }
  if (sourceEventId !== '') {
    return {
      isSourceEventIdAvailable: 'Yes',
      sourceEventId: sourceEventId,
      comments: ''
    };
  } else {
    return {
      isSourceEventIdAvailable: 'No',
      sourceEventId: '',
      comments: comments
    };
  }
}
